import {
  apiResponseEnvelopeSchema,
  styleTransferErrorSchema
} from './contracts/styleTransferSchemas';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface HttpRequestOptions<TBody = unknown> extends RequestInit {
  /** JSON body for the request. Automatically stringified. */
  json?: TBody;
  /** Abort signal for cancellation. */
  signal?: AbortSignal;
  /** Optional override for the base URL (defaults to VITE_API_BASE_URL). */
  baseUrl?: string;
}

export interface HttpClientConfig {
  /** Default headers merged into each request. */
  headers?: Record<string, string>;
  /** Request timeout in milliseconds. */
  timeoutMs?: number;
}

export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly meta?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class HttpClient {
  private readonly defaultHeaders: Record<string, string>;
  private readonly timeoutMs: number;

  constructor(config: HttpClientConfig = {}) {
    this.defaultHeaders = config.headers ?? { 'Content-Type': 'application/json' };
    this.timeoutMs = config.timeoutMs ?? 15000;
  }

  private sanitizeHeaders(headers: Record<string, string>) {
    const MASK_HEADERS = new Set(['authorization', 'x-api-token']);
    return Object.fromEntries(
      Object.entries(headers).map(([key, value]) => {
        if (!value) {
          return [key, value];
        }
        if (MASK_HEADERS.has(key.toLowerCase())) {
          const prefix = value.slice(0, 6);
          const suffix = value.slice(-4);
          return [key, `${prefix}…${suffix}`];
        }
        return [key, value];
      })
    );
  }

  private describeBody(options: HttpRequestOptions) {
    if (options.json !== undefined) {
      return options.json;
    }

    if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
      const entries: Record<string, unknown> = {};
      for (const [key, rawValue] of options.body.entries()) {
        if (rawValue instanceof File) {
          entries[key] = `File(${rawValue.name}, ${rawValue.size} bytes)`;
        } else {
          entries[key] = rawValue;
        }
      }
      return { type: 'FormData', entries };
    }

    if (typeof options.body === 'string') {
      return { type: 'text', length: options.body.length };
    }

    if (options.body) {
      return { type: options.body.constructor?.name ?? 'UnknownBody' };
    }

    return undefined;
  }

  async request<TResponse, TBody = unknown>(
    method: HttpMethod,
    path: string,
    options: HttpRequestOptions<TBody> = {}
  ): Promise<TResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const baseUrl = options.baseUrl ?? import.meta.env.VITE_API_BASE_URL;
      if (!baseUrl) {
        throw new Error('VITE_API_BASE_URL is not defined');
      }

      const url = new URL(path, baseUrl);

      const headers = {
        ...this.defaultHeaders,
        ...options.headers
      };

      const shouldStripContentType =
        (method === 'GET' || method === 'HEAD') &&
        options.json === undefined &&
        options.body === undefined;
      const isFormDataBody =
        typeof FormData !== 'undefined' && options.body instanceof FormData;

      if (shouldStripContentType || isFormDataBody) {
        delete headers['Content-Type'];
      }

      const init: RequestInit = {
        ...options,
        method,
        headers,
        signal: this.mergeSignals(controller.signal, options.signal)
      };

      if (options.json !== undefined) {
        init.body = JSON.stringify(options.json);
      }

      const logTimestamp = new Date().toISOString();
      const logPayload = {
        timestamp: logTimestamp,
        method,
        url: url.toString(),
        headers: this.sanitizeHeaders(headers),
        body: path.startsWith('/api/v1/auth/google/') ? undefined : this.describeBody(options)
      };
      console.info('[http] request', logPayload);

      const response = await fetch(url, init);
      const contentType = response.headers.get('Content-Type') ?? '';

      if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        let meta: Record<string, unknown> | undefined;

        if (contentType.includes('application/json')) {
          try {
            const payload = await response.json();
            const envelope = apiResponseEnvelopeSchema.safeParse(payload);
            const errorSource = envelope.success ? envelope.data : payload;
            const parsed = styleTransferErrorSchema.safeParse(errorSource);
            if (parsed.success) {
              message =
                parsed.data.message ??
                (envelope.success ? envelope.data.message ?? message : message);
              meta = parsed.data;
            } else if (envelope.success) {
              message = envelope.data.message ?? message;
              meta = envelope.data as Record<string, unknown>;
            } else {
              meta = payload as Record<string, unknown>;
            }
          } catch (error) {
            meta = { error };
          }
        }

        throw new HttpError(message, response.status, meta);
      }

      if (response.status === 204 || !contentType) {
        return undefined as TResponse;
      }

      if (contentType.includes('application/json')) {
        const payload = await response.json();
        const envelope = apiResponseEnvelopeSchema.safeParse(payload);
        if (envelope.success) {
          return envelope.data.data as TResponse;
        }
        return payload as TResponse;
      }

      return (await response.text()) as TResponse;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  get<TResponse>(path: string, options?: HttpRequestOptions) {
    return this.request<TResponse>('GET', path, options);
  }

  post<TResponse, TBody = unknown>(path: string, options?: HttpRequestOptions<TBody>) {
    return this.request<TResponse, TBody>('POST', path, options);
  }

  put<TResponse, TBody = unknown>(path: string, options?: HttpRequestOptions<TBody>) {
    return this.request<TResponse, TBody>('PUT', path, options);
  }

  patch<TResponse, TBody = unknown>(path: string, options?: HttpRequestOptions<TBody>) {
    return this.request<TResponse, TBody>('PATCH', path, options);
  }

  delete<TResponse>(path: string, options?: HttpRequestOptions) {
    return this.request<TResponse>('DELETE', path, options);
  }

  private mergeSignals(timeoutSignal: AbortSignal, userSignal?: AbortSignal) {
    if (!userSignal) {
      return timeoutSignal;
    }

    if (userSignal.aborted) {
      timeoutSignal.throwIfAborted();
      return userSignal;
    }

    const controller = new AbortController();

    timeoutSignal.addEventListener('abort', () => controller.abort(timeoutSignal.reason), {
      once: true
    });

    userSignal.addEventListener('abort', () => controller.abort(userSignal.reason), {
      once: true
    });

    return controller.signal;
  }
}

export const httpClient = new HttpClient();
